/**
 * Rate Limiter Utility
 * Prevents abuse by limiting API calls per user/IP
 */

// In-memory store (for single-instance apps)
// For production with multiple servers, use Redis or Supabase
const requestCounts = new Map();

/**
 * Rate limit check
 * @param {string} identifier - User ID or IP address
 * @param {number} maxRequests - Maximum requests allowed
 * @param {number} windowMs - Time window in milliseconds
 * @returns {boolean} - true if allowed, false if rate limited
 */
export function checkRateLimit(identifier, maxRequests = 100, windowMs = 60000) {
  const now = Date.now();
  const key = identifier;
  
  // Get existing requests for this identifier
  const requests = requestCounts.get(key) || [];
  
  // Remove old requests outside the time window
  const recentRequests = requests.filter(time => now - time < windowMs);
  
  // Check if limit exceeded
  if (recentRequests.length >= maxRequests) {
    return false;
  }
  
  // Add current request
  recentRequests.push(now);
  requestCounts.set(key, recentRequests);
  
  return true;
}

/**
 * Rate limit wrapper for async functions
 * @param {Function} fn - Function to rate limit
 * @param {Object} options - Rate limit options
 * @param {number} options.maxRequests - Max requests per window
 * @param {number} options.windowMs - Time window in ms
 * @param {Function} options.getIdentifier - Function to get identifier (user ID, IP, etc.)
 * @param {string} options.errorMessage - Custom error message
 */
export function withRateLimit(fn, options = {}) {
  const {
    maxRequests = 100,
    windowMs = 60000,
    getIdentifier = () => 'anonymous',
    errorMessage = 'Rate limit exceeded. Please try again later.'
  } = options;
  
  return async (...args) => {
    const identifier = await getIdentifier(...args);
    
    if (!checkRateLimit(identifier, maxRequests, windowMs)) {
      throw new Error(errorMessage);
    }
    
    return fn(...args);
  };
}

/**
 * Clear rate limit for an identifier (useful for testing or manual reset)
 */
export function clearRateLimit(identifier) {
  requestCounts.delete(identifier);
}

/**
 * Get current request count for an identifier
 */
export function getRequestCount(identifier, windowMs = 60000) {
  const now = Date.now();
  const requests = requestCounts.get(identifier) || [];
  const recentRequests = requests.filter(time => now - time < windowMs);
  return recentRequests.length;
}

/**
 * Clean up old entries periodically (prevent memory leak)
 */
setInterval(() => {
  const now = Date.now();
  const maxAge = 3600000; // 1 hour
  
  for (const [key, requests] of requestCounts.entries()) {
    const recentRequests = requests.filter(time => now - time < maxAge);
    if (recentRequests.length === 0) {
      requestCounts.delete(key);
    } else {
      requestCounts.set(key, recentRequests);
    }
  }
}, 300000); // Clean every 5 minutes

